import React, { useMemo } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import {
  DATE_PRESETS, SLA_OPTIONS, STATUS_OPTIONS, rangeForPreset, activeFilterCount,
} from "../lib/filters";
import { Search, X, Calendar, RotateCcw } from "lucide-react";

/**
 * Compact, sticky filter bar shared across every queue / dashboard.
 *
 * Layout:
 *   Row 1  Universal search (50% width desktop · full mobile · sticky)
 *   Row 2  Date preset pills + Custom popover  |  Location · DSE · Status · SLA  |  Clear · Export
 *   Row 3  Active filter chips (auto-hides when empty)
 *
 * Props:
 *   filters          — current filter state (see lib/filters defaultFilters)
 *   onChange(f)      — fires on every change
 *   onReset()        — wipes filters back to defaults
 *   placeholder      — search placeholder
 *   showStatus       — render Status dropdown (default true)
 *   showSla          — render SLA dropdown   (default true)
 *   showLocation     — render Location dropdown (default false)
 *   showDse          — render DSE dropdown   (default false)
 *   statusOptions    — override the default Pending/Completed pair
 *   locationOptions  — [{ id, code, name }]
 *   dseOptions       — [{ id, name }]
 *   rightSlot        — usually <ExportButton ...>
 *   sticky           — when true, sticks at top:64 on scroll (default false)
 */
export default function FilterBar({
  filters, onChange, onReset,
  placeholder = "Search SR / Customer / Mobile / VIN / Regn / Ledger",
  showStatus = true, showSla = true,
  showLocation = false, showDse = false,
  statusOptions = null, locationOptions = [], dseOptions = [],
  rightSlot, sticky = false,
}) {
  const statusList = statusOptions || STATUS_OPTIONS;
  const count = activeFilterCount(filters);

  const setPreset = (p) => {
    const r = rangeForPreset(p);
    onChange({ ...filters, preset: p, date_from: r.date_from, date_to: r.date_to });
  };
  const isCustom = !filters.preset && (filters.date_from || filters.date_to);

  // ─── Active chips ───────────────────────────────────────────────
  const chips = useMemo(() => {
    const c = [];
    if (filters.preset) {
      c.push({ key: "preset", label: DATE_PRESETS.find((p) => p.value === filters.preset)?.label || filters.preset,
               clear: () => onChange({ ...filters, preset: null, date_from: null, date_to: null }) });
    } else if (filters.date_from || filters.date_to) {
      c.push({ key: "custom", label: `${filters.date_from || "…"} → ${filters.date_to || "…"}`,
               clear: () => onChange({ ...filters, date_from: null, date_to: null }) });
    }
    if (filters.location_id) {
      const loc = locationOptions.find((l) => l.id === filters.location_id);
      c.push({ key: "location", label: loc?.code || "Location",
               clear: () => onChange({ ...filters, location_id: null }) });
    }
    if (filters.dse_id) {
      const dse = dseOptions.find((d) => d.id === filters.dse_id);
      c.push({ key: "dse", label: dse?.name || "DSE",
               clear: () => onChange({ ...filters, dse_id: null }) });
    }
    if (filters.status) {
      const so = statusList.find((s) => s.value === filters.status);
      c.push({ key: "status", label: so?.label || filters.status,
               clear: () => onChange({ ...filters, status: null }) });
    }
    if (filters.sla_status) {
      const so = SLA_OPTIONS.find((s) => s.value === filters.sla_status);
      c.push({ key: "sla", label: `SLA · ${so?.label || filters.sla_status}`,
               clear: () => onChange({ ...filters, sla_status: null }) });
    }
    if ((filters.search || "").trim()) {
      c.push({ key: "search", label: `“${filters.search.trim()}”`,
               clear: () => onChange({ ...filters, search: "" }) });
    }
    return c;
  }, [filters, locationOptions, dseOptions, statusList, onChange]);

  return (
    <div
      className={`flex flex-col gap-2.5 rounded-lg border border-slate-200 bg-white px-3 py-3 shadow-od-subtle sm:px-4 ${
        sticky ? "sticky top-16 z-20" : ""
      }`}
      data-testid="filter-bar"
    >
      {/* Row 1 — Universal Search */}
      <div className="relative w-full md:max-w-[50%]">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={filters.search || ""}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder={placeholder}
          className="h-10 pl-9"
          data-testid="filter-search"
        />
      </div>

      {/* Row 2 — Date presets · Operational dropdowns · Actions */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Date presets */}
        <div className="flex items-center gap-1 overflow-x-auto md:overflow-visible -mx-1 px-1">
          {DATE_PRESETS.map((p) => {
            const active = filters.preset === p.value;
            return (
              <button
                key={p.value} type="button"
                onClick={() => setPreset(active ? null : p.value)}
                data-testid={`filter-preset-${p.value}`}
                className={`h-9 shrink-0 rounded-full border px-3 text-xs font-semibold transition-colors ${
                  active
                    ? "border-primary bg-primary text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-primary/40 hover:bg-slate-50"
                }`}
              >
                {p.label}
              </button>
            );
          })}
          {/* Custom popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={isCustom ? "default" : "outline"}
                size="sm"
                className="h-9 shrink-0 gap-1.5 rounded-full px-3"
                data-testid="filter-custom-date-trigger"
              >
                <Calendar className="h-3.5 w-3.5" /> Custom
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 space-y-2 p-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">From</label>
                <Input
                  type="date" value={filters.date_from || ""}
                  onChange={(e) => onChange({ ...filters, preset: null, date_from: e.target.value || null })}
                  data-testid="filter-custom-from"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">To</label>
                <Input
                  type="date" value={filters.date_to || ""}
                  onChange={(e) => onChange({ ...filters, preset: null, date_to: e.target.value || null })}
                  data-testid="filter-custom-to"
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <span className="hidden h-6 w-px bg-slate-200 sm:block" aria-hidden="true" />

        {/* Operational dropdowns — horizontally scrollable on mobile */}
        <div className="flex flex-1 items-center gap-2 overflow-x-auto md:overflow-visible -mx-1 px-1">
          {showLocation && (
            <Select
              value={filters.location_id || "all"}
              onValueChange={(v) => onChange({ ...filters, location_id: v === "all" ? null : v })}
            >
              <SelectTrigger className="h-9 w-[140px] shrink-0" data-testid="filter-location">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locationOptions.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.code} — {l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {showDse && (
            <Select
              value={filters.dse_id || "all"}
              onValueChange={(v) => onChange({ ...filters, dse_id: v === "all" ? null : v })}
            >
              <SelectTrigger className="h-9 w-[140px] shrink-0" data-testid="filter-dse">
                <SelectValue placeholder="DSE" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All DSE</SelectItem>
                {dseOptions.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {showStatus && (
            <Select
              value={filters.status || "all"}
              onValueChange={(v) => onChange({ ...filters, status: v === "all" ? null : v })}
            >
              <SelectTrigger className="h-9 w-[140px] shrink-0" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Status</SelectItem>
                {statusList.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {showSla && (
            <Select
              value={filters.sla_status || "all"}
              onValueChange={(v) => onChange({ ...filters, sla_status: v === "all" ? null : v })}
            >
              <SelectTrigger className="h-9 w-[130px] shrink-0" data-testid="filter-sla">
                <SelectValue placeholder="SLA" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any SLA</SelectItem>
                {SLA_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Right side — Clear + Export */}
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={count === 0}
            onClick={onReset}
            className="h-9 gap-1 text-slate-500 hover:text-slate-700"
            data-testid="filter-reset"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Clear
          </Button>
          {rightSlot}
        </div>
      </div>

      {/* Row 3 — Active filter chips */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-2.5"
             data-testid="filter-chips">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 pr-1">Active</span>
          {chips.map((c) => (
            <button
              key={c.key} type="button" onClick={c.clear}
              data-testid={`chip-${c.key}`}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 transition-colors"
            >
              {c.label}
              <X className="h-3 w-3 opacity-60" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
